import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { reasonMessage } from '../../i18n';

import type { SaveState } from './use-save-settings';

/**
 * Result of the last save: the R15 notice, an exceeded limit (R13) or the failure (I12).
 *
 * R15's second sentence depends on `openPositionCount`, which C1 does not return yet (D3).
 * Without it, E7 says the sentence that is true whatever the count ("Positions already open on
 * this channel keep their size", FXM-02 §6 case 5); with it, the R15 wording applies.
 */
export function SaveOutcome({
  state,
  openPositionCount,
}: {
  readonly state: SaveState;
  readonly openPositionCount: number | undefined;
}): ReactElement | null {
  const { t } = useTranslation();
  const settingsNotice = (
    <Alert severity="success">
      {t('settings.saved.next')}{' '}
      {openPositionCount === undefined
        ? t('settings.saved.openPositionsUnknown')
        : openPositionCount > 0 && t('settings.saved.openPositions')}
    </Alert>
  );
  switch (state.status) {
    case 'idle':
    case 'saving':
      return null;
    case 'saved': {
      const exceeded = reasonMessage('DAILY_LOSS_LIMIT_ALREADY_EXCEEDED');
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {state.version === null ? (
            <Alert severity="success">{t('settings.saved.limitOnly')}</Alert>
          ) : (
            settingsNotice
          )}
          {state.limits?.limitAlreadyReached === true && (
            <Alert severity="warning">{t(exceeded.key, exceeded.values)}</Alert>
          )}
        </Box>
      );
    }
    case 'failed': {
      // Only the translated reason, never a raw message (I9, I12).
      const message = reasonMessage(state.error.reasonCode, state.error.params);
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {state.settingsSaved !== null && settingsNotice}
          <Alert severity="error">{t(message.key, message.values)}</Alert>
        </Box>
      );
    }
  }
}
